import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { Temporal } from 'temporal-polyfill';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { AnalyticsPeriod, FeatureProjectAnalyticsState, PERIODS } from './features/feature-project-analytics.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const DEFAULT_PERIOD: AnalyticsPeriod = 'day';
const DEFAULT_CLASSIFICATION: WireTypes.OutcomeClassification = 'worst';

function isOutcomeOrEmpty(text: string): text is WireTypes.AnalyticsOutcome|'' {
  return text === '' || WireTypes.ANALYTICS_OUTCOMES.includes(text as WireTypes.AnalyticsOutcome);
}

// Every filter is `from>to`, and a side is left empty when the test did not report in that
// period: `expected>flaked,>flaked` is "was passing, now flaky" and "was not reported, now flaky".
function encodeTransitions(filters: WireTypes.TransitionFilter[]): string {
  return filters.map(filter => `${filter.from ?? ''}>${filter.to ?? ''}`).join(',');
}

// Anything malformed decodes to no selection at all, rather than to a different one.
function decodeTransitions(text: string): WireTypes.TransitionFilter[]|undefined {
  const filters: WireTypes.TransitionFilter[] = [];
  for (const entry of text.split(',')) {
    const [from, to, ...rest] = entry.split('>');
    if (to === undefined || rest.length || !isOutcomeOrEmpty(from) || !isOutcomeOrEmpty(to))
      return undefined;
    filters.push({ from: from || undefined, to: to || undefined });
  }
  return filters.length ? filters : undefined;
}

const pageSchema = {
  head: URLOptions.head,
  period: URLState.option<AnalyticsPeriod>({
    name: 'period',
    default: DEFAULT_PERIOD,
    encode: period => period,
    decode: text => PERIODS.find(period => period === text) ?? DEFAULT_PERIOD,
  }),
  classification: URLState.option<WireTypes.OutcomeClassification>({
    name: 'classification',
    default: DEFAULT_CLASSIFICATION,
    encode: classification => classification,
    decode: text => WireTypes.OUTCOME_CLASSIFICATIONS.find(classification => classification === text) ?? DEFAULT_CLASSIFICATION,
  }),
  until: URLOptions.until,
  selected: URLState.option<Temporal.PlainDate|undefined>({
    name: 'selected',
    default: undefined,
    encode: day => day?.toString(),
    decode: text => text ? Temporal.PlainDate.from(text) : undefined,
  }),
  query: URLOptions.query,
  transitions: URLState.option<WireTypes.TransitionFilter[]|undefined>({
    name: 'tests',
    default: undefined,
    encode: filters => filters?.length ? encodeTransitions(filters) : undefined,
    decode: text => decodeTransitions(text),
  }),
  timelineSplit: URLOptions.timelineSplit,
};

@customElement('page-project-analytics')
export class PageProjectAnalytics extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, head?: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'analytics'].join('/'), URLState.toSearchParams(pageSchema, {
      head: options.head,
    }), options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/analytics',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-analytics
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-analytics>
      `),
    }];
  }

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  private _urlState = new URLState(this, pageSchema);

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'Analytics',
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;

    const linkRenderer = this._urlState.createLinkRenderer();
    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"analytics"}
      ></project-header>
      <project-body wide user-role=${this._project.access}>
        <feature-project-analytics
          .state=${{
            timelineSplit: this._urlState.state.timelineSplit,
            head: this._urlState.state.head,
            period: this._urlState.state.period,
            classification: this._urlState.state.classification,
            until: this._urlState.state.until,
            selected: this._urlState.state.selected,
            transitions: this._urlState.state.transitions,
            query: this._urlState.state.query,
          } satisfies FeatureProjectAnalyticsState}
          .linkifier=${(options: FeatureProjectAnalyticsState) => linkRenderer({
            head: options.head,
            period: options.period,
            classification: options.classification,
            until: options.until,
            selected: options.selected,
            transitions: options.transitions,
            query: options.query,
            timelineSplit: options.timelineSplit,
          })}
        ></feature-project-analytics>
      </project-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles];
}
